import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";
import {
  REPUTATION_UPGRADES, nextReputationCost,
  type ReputationUpgradeDef, type ReputationUpgradeId,
} from "../core/roscha";

interface UpgradeRefs {
  id: ReputationUpgradeId;
  def: ReputationUpgradeDef;
  card: HTMLElement;
  levelEl: HTMLElement;
  actionBtn: HTMLButtonElement;
}

export class GuildOverlay extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;
  private reputationEl?: HTMLElement;
  private upgrades: UpgradeRefs[] = [];
  private unsubWorld?: () => void;
  private onWake = () => this.refresh();

  constructor() {
    super("GuildOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.mount();
    this.refresh();
    this.unsubWorld = this.app.onWorldState(() => this.refresh());
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private mount() {
    const upgradeIds = Object.keys(REPUTATION_UPGRADES) as ReputationUpgradeId[];
    const cardsHtml = upgradeIds
      .map((id) => {
        const def = REPUTATION_UPGRADES[id];
        return `
          <div class="guild__card" data-upgrade-card="${id}">
            <div class="guild__card-head">
              <h3 class="guild__card-name">${def.name}</h3>
              <span class="guild__card-level" data-upgrade-level="${id}">0 / ${def.costs.length}</span>
            </div>
            <p class="guild__card-desc">${def.description}</p>
            <button class="dom-btn guild__card-action" data-upgrade-id="${id}">…</button>
          </div>
        `;
      })
      .join("");

    const bodyHtml = `
      <div class="dom-resource-row">
        <span>Репутация <strong data-bind="reputation">0</strong></span>
      </div>
      <div class="guild__grid">${cardsHtml}</div>
    `;

    this.overlay = mountOverlay(this, {
      title: "Гильдия авантюристов",
      subtitle: "Глобальные апгрейды за репутацию",
      bodyHtml,
      onClose: () => this.app.openTown(),
      inBody: true,
    });

    const root = this.overlay.root;
    this.reputationEl = root.querySelector<HTMLElement>('[data-bind="reputation"]') ?? undefined;
    this.upgrades = upgradeIds.map((id) => {
      const def = REPUTATION_UPGRADES[id];
      const card = root.querySelector<HTMLElement>(`[data-upgrade-card="${id}"]`)!;
      const levelEl = root.querySelector<HTMLElement>(`[data-upgrade-level="${id}"]`)!;
      const actionBtn = root.querySelector<HTMLButtonElement>(`[data-upgrade-id="${id}"]`)!;
      actionBtn.addEventListener("click", () => {
        if (actionBtn.disabled) return;
        this.app.applyReputationUpgrade(id);
      });
      return { id, def, card, levelEl, actionBtn };
    });
  }

  private refresh() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.overlay) return;

    if (this.reputationEl) this.reputationEl.textContent = String(hero.reputation);

    for (const u of this.upgrades) {
      const level = hero.reputationUpgrades[u.id] ?? 0;
      const cost = nextReputationCost(u.def, level);
      const maxLevel = u.def.costs.length;
      const canBuy = cost !== null && hero.reputation >= cost;

      u.levelEl.textContent = `${level} / ${maxLevel}`;
      u.actionBtn.disabled = !canBuy;
      u.actionBtn.setAttribute("data-disabled", String(!canBuy));
      u.actionBtn.classList.toggle("dom-btn--success", canBuy);
      if (cost === null) u.actionBtn.textContent = "Максимум";
      else if (!canBuy) u.actionBtn.textContent = `Нужно ${cost} репутации`;
      else u.actionBtn.textContent = `Улучшить · ${cost}`;
    }
  }

  private cleanup() {
    this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
    this.unsubWorld?.();
    this.unsubWorld = undefined;
    this.overlay = undefined;
    this.reputationEl = undefined;
    this.upgrades = [];
  }
}
